import React, { useState, useMemo } from 'react';
import { LabValue } from '../types';
import { ValueCorrectionModal } from './ValueCorrectionModal';
import { ExtractionValidationService, ValidationResult } from '../services/extractionValidationService';
import { ZeroValueIndicator } from './ZeroValueIndicator';

export interface DataTableProps {
  results: Map<string, LabValue>;
  onMarkerClick?: (marker: string, value: number, units: string) => void;
  selectedMarker?: string | null;
  onValueCorrected?: (marker: string, correctedValue: number, correctedUnit?: string) => void;
}


// Helper function to get status from LLM-provided qualitative result
const getResultStatus = (qualitativeResult?: string): 'normal' | 'high' | 'low' | 'unknown' => {
  if (!qualitativeResult) {
    return 'unknown';
  }
  
  const result = qualitativeResult.toUpperCase();
  
  // Check for full words or abbreviations
  if (result === 'HIGH' || result === 'H') {
    return 'high';
  }
  if (result === 'LOW' || result === 'L') {
    return 'low';
  }
  if (result === 'NORMAL' || result === 'N') {
    return 'normal';
  }
  
  return 'unknown';
};

// Component for rendering the reference range bar (shown on row click)
const ReferenceRangeBar: React.FC<{
  value: number;
  referenceRange: string;
  units: string;
  marker: string;
  status?: string;
}> = ({ value, referenceRange, units, marker, status: providedStatus }) => {
  const barData = React.useMemo(() => {
    if (!referenceRange || referenceRange === 'N/A') {
      return { canShowBar: false };
    }

    // Remove units from reference range if present (e.g., "70-99 mg/dL" -> "70-99")
    const range = referenceRange.replace(/\s*[a-zA-Z/%]+.*$/, '').trim();
    
    // Format: "70-100" or "70 - 100"
    const rangeMatch = range.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);
    if (rangeMatch) {
      const min = parseFloat(rangeMatch[1]);
      const max = parseFloat(rangeMatch[2]);
      
      // Use provided status or determine from value
      let status: 'normal' | 'high' | 'low' = 'normal';
      if (providedStatus) {
        const normalizedStatus = providedStatus.toUpperCase();
        if (normalizedStatus === 'HIGH' || normalizedStatus === 'H') status = 'high';
        else if (normalizedStatus === 'LOW' || normalizedStatus === 'L') status = 'low';
        else status = 'normal';
      } else {
        // Fallback to calculation if no status provided
        if (value < min) status = 'low';
        else if (value > max) status = 'high';
      }
      
      // Create extended range to ensure space for out-of-range values
      const rangeWidth = max - min;
      
      // Calculate display range with improved padding logic
      let displayMin = min;
      let displayMax = max;
      
      // Always add some base padding for visual clarity
      const basePadding = rangeWidth * 0.15; // 15% padding on each side
      
      if (value < min) {
        // Value is below minimum
        const distanceBelowMin = min - value;
        // Ensure enough space to show the value with at least 20% of bar width
        const leftPadding = Math.max(distanceBelowMin + rangeWidth * 0.2, rangeWidth * 0.5);
        displayMin = min - leftPadding;
        displayMax = max + basePadding;
      } else if (value > max) {
        // Value is above maximum
        const distanceAboveMax = value - max;
        // Ensure enough space to show the value with at least 20% of bar width
        const rightPadding = Math.max(distanceAboveMax + rangeWidth * 0.2, rangeWidth * 0.5);
        displayMin = min - basePadding;
        displayMax = max + rightPadding;
      } else {
        // Value is within range, add symmetric padding
        displayMin = min - basePadding;
        displayMax = max + basePadding;
      }
      
      // Calculate position within the extended range
      const totalDisplayRange = displayMax - displayMin;
      const normalRangeStart = ((min - displayMin) / totalDisplayRange) * 100;
      const normalRangeEnd = ((max - displayMin) / totalDisplayRange) * 100;
      const valuePosition = ((value - displayMin) / totalDisplayRange) * 100;
      
      // Don't clamp position - let it show naturally within the padded range
      
      return {
        canShowBar: true,
        min,
        max,
        value,
        position: valuePosition,
        status,
        displayMin,
        displayMax,
        normalRangeStart,
        normalRangeEnd,
        minLabel: `${min}${units}`,
        maxLabel: `${max}${units}`,
        valueLabel: `${value}${units}`,
        isOutsideRange: value < min || value > max
      };
    }
    
    // Format: ">40" or "> 40"
    const greaterMatch = range.match(/^>\s*(\d+(?:\.\d+)?)$/);
    if (greaterMatch) {
      const threshold = parseFloat(greaterMatch[1]);
      const visualMax = Math.max(threshold * 2, value * 1.2);
      const position = ((value - threshold) / (visualMax - threshold)) * 100;
      
      return {
        canShowBar: true,
        min: threshold,
        max: visualMax,
        value,
        position: Math.max(0, Math.min(100, position)),
        status: providedStatus ? (providedStatus.toUpperCase() === 'LOW' || providedStatus.toUpperCase() === 'L' ? 'low' : 'normal') : (value > threshold ? 'normal' : 'low'),
        displayMin: threshold,
        displayMax: visualMax,
        minLabel: `>${threshold}${units}`,
        maxLabel: `${visualMax}${units}`,
        valueLabel: `${value}${units}`,
        isOutsideRange: value <= threshold
      };
    }
    
    // Format: "<100" or "< 100"
    const lessMatch = range.match(/^<\s*(\d+(?:\.\d+)?)$/);
    if (lessMatch) {
      const threshold = parseFloat(lessMatch[1]);
      const visualMin = Math.min(0, value * 0.8);
      const position = ((value - visualMin) / (threshold - visualMin)) * 100;
      
      return {
        canShowBar: true,
        min: visualMin,
        max: threshold,
        value,
        position: Math.max(0, Math.min(100, position)),
        status: providedStatus ? (providedStatus.toUpperCase() === 'HIGH' || providedStatus.toUpperCase() === 'H' ? 'high' : 'normal') : (value < threshold ? 'normal' : 'high'),
        displayMin: visualMin,
        displayMax: threshold,
        minLabel: `${visualMin}${units}`,
        maxLabel: `<${threshold}${units}`,
        valueLabel: `${value}${units}`,
        isOutsideRange: value >= threshold
      };
    }
    
    return { canShowBar: false };
  }, [value, referenceRange, units, providedStatus]);
  
  if (!barData.canShowBar) {
    return (
      <div className="reference-range-display">
        <h4>{marker} - Reference Range</h4>
        <p>Reference range not available for this marker.</p>
      </div>
    );
  }
  
  return (
    <div className="reference-range-display">
      <h4>{marker} - Reference Range Analysis</h4>
      <div className="range-bar-container">
        <div className="range-bar-wrapper">
          <div className="range-bar">
            {/* Show the normal range zone if we have the data */}
            {barData.normalRangeStart !== undefined && barData.normalRangeEnd !== undefined && (
              <div 
                className="normal-range-zone"
                style={{ 
                  left: `${barData.normalRangeStart}%`,
                  width: `${barData.normalRangeEnd - barData.normalRangeStart}%`
                }}
              >
                <span className="range-boundary-label left">Low</span>
                <span className="range-boundary-label right">High</span>
              </div>
            )}
            <div
              className={`range-marker ${barData.status} ${barData.isOutsideRange ? 'outside-range' : ''}`}
              style={{ left: `${barData.position}%` }}
            >
              <div className="marker-dot"></div>
              <div className="marker-label">{barData.valueLabel}</div>
            </div>
          </div>
          {/* Range values positioned below the bar */}
          <div className="range-values-labels">
            <span 
              className="range-value-label low"
              style={{ left: `${barData.normalRangeStart}%` }}
            >
              {barData.minLabel}
            </span>
            <span 
              className="range-value-label high"
              style={{ left: `${barData.normalRangeEnd}%` }}
            >
              {barData.maxLabel}
            </span>
          </div>
        </div>
        <div className="range-status-text">
          <span className={`status-text ${barData.status || 'unknown'}`}>
            Your result ({barData.valueLabel}) is <strong>{(barData.status || 'unknown').toUpperCase()}</strong>
            {barData.isOutsideRange && (
              <span className="outside-range-note"> (outside normal range)</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Component for displaying lab results in a table format
 */
const DataTable: React.FC<DataTableProps> = ({ results, onMarkerClick, selectedMarker, onValueCorrected }) => {
  // Convert Map to array for rendering
  const resultsArray = Array.from(results.entries());
  const [selectedRowForRange, setSelectedRowForRange] = React.useState<string | null>(null);
  const [showProblemsOnly, setShowProblemsOnly] = useState(true); // Default to showing problems only
  
  // State for correction modal
  const [correctionModalData, setCorrectionModalData] = useState<{
    marker: string;
    labValue: LabValue;
    validation: ValidationResult;
  } | null>(null);
  
  // Validate all results
  const validationResults = useMemo(() => {
    return ExtractionValidationService.validateLabResults(results);
  }, [results]);
  
  // Get problematic markers
  const problematicMarkers = useMemo(() => {
    return ExtractionValidationService.getProblematicMarkers(validationResults);
  }, [validationResults]);
  
  // Filter results based on view mode
  const displayResults = useMemo(() => {
    if (!showProblemsOnly) {
      return resultsArray;
    }
    return resultsArray.filter(([marker]) => problematicMarkers.includes(marker));
  }, [resultsArray, showProblemsOnly, problematicMarkers]);

  // Get the selected row data for the reference range bar
  const selectedRowData = selectedRowForRange ? results.get(selectedRowForRange) : null;

  const handleRowClick = (marker: string, data: LabValue) => {
    // Toggle reference range bar
    setSelectedRowForRange(selectedRowForRange === marker ? null : marker);
    
    // Also trigger the original onMarkerClick for LLM explanation
    if (onMarkerClick && data.value && data.units) {
      onMarkerClick(marker, data.value, data.units);
    }
  };

  return (
    <div className="data-table-container">
      {/* Filter Toggle */}
      {problematicMarkers.length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-amber-900">
                  {problematicMarkers.length} test{problematicMarkers.length !== 1 ? 's' : ''} may have extraction errors
                </h3>
                <p className="text-xs text-amber-700 mt-0.5">
                  Review these values carefully and correct if needed
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowProblemsOnly(!showProblemsOnly)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d={showProblemsOnly ? "M4 6h16M4 12h16M4 18h16" : "M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"} />
              </svg>
              <span className="text-sm font-medium text-amber-800">
                {showProblemsOnly ? 'Show All Results' : 'Show Problems Only'}
              </span>
            </button>
          </div>
        </div>
      )}
      <table className="w-full bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test Name</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody>
          {displayResults.length > 0 ? (
            displayResults.map(([marker, data]) => {
              const validation = validationResults.get(marker);
              const hasError = validation?.issues.some(issue => issue.severity === 'error');
              const hasWarning = validation?.issues.some(issue => issue.severity === 'warning');
              
              return (
              <tr
                key={marker}
                className={`
                  border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer
                  ${selectedRowForRange === marker ? 'bg-blue-50' : ''}
                `.trim()}
                onClick={() => handleRowClick(marker, data)}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{marker}</span>
                      {hasError && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Unit Error
                        </span>
                      )}
                      {!hasError && hasWarning && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Low Confidence
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Display value with corrections if applicable */}
                      {data.corrected_value !== undefined ? (
                        <div className="flex items-center gap-2">
                          <span className="line-through text-gray-400 text-sm">
                            {data.value} {data.units}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="font-medium text-green-700 flex items-center gap-1">
                            {data.corrected_value} {data.corrected_units || data.units}
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        </div>
                      ) : data.value !== undefined && data.value !== null ? (
                        // Show numeric value with units, including zero values
                        <div className="flex items-center">
                          <span className="font-medium text-gray-900">
                            {data.value} {data.units || ''}
                          </span>
                          {data.value === 0 && <ZeroValueIndicator testName={marker} value={data.value} />}
                        </div>
                      ) : data.qualitative_result !== undefined && data.qualitative_result !== null ? (
                        // Only show qualitative result if there's no numeric value
                        <span className="font-medium text-gray-900">
                          {data.qualitative_result}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                    
                    {/* Show confidence for problematic values */}
                    {validation && validation.hasIssues && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {validation.confidence}% confidence
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCorrectionModalData({ marker, labValue: data, validation });
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Fix
                        </button>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {/* Display color-coded status badge based on LLM-provided status */}
                  {(() => {
                    const status = getResultStatus(data.qualitative_result);
                    if (status === 'normal') {
                      return (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Normal
                        </span>
                      );
                    } else if (status === 'high') {
                      return (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          High
                        </span>
                      );
                    } else if (status === 'low') {
                      return (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Low
                        </span>
                      );
                    } else {
                      return <span className="text-gray-400">-</span>;
                    }
                  })()}
                </td>
              </tr>
            );
            })
          ) : (
            <tr>
              <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                {showProblemsOnly ? 'No extraction errors detected' : 'No values extracted'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
      {/* Reference Range Bar - shown when a row is selected */}
      {selectedRowForRange && selectedRowData && selectedRowData.reference_range &&
       (typeof selectedRowData.value === 'number' || typeof selectedRowData.corrected_value === 'number') && (
        <div className="reference-range-section">
          <ReferenceRangeBar
            value={selectedRowData.corrected_value ?? selectedRowData.value}
            referenceRange={selectedRowData.reference_range}
            units={selectedRowData.units || ''}
            marker={selectedRowForRange}
            status={selectedRowData.qualitative_result}
          />
        </div>
      )}
      
      {/* Detailed Issues Panel - Show when viewing all results */}
      {!showProblemsOnly && problematicMarkers.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Extraction Issues Detail</h3>
          <div className="space-y-3">
            {problematicMarkers.map(marker => {
              const validation = validationResults.get(marker);
              const labValue = results.get(marker);
              if (!validation || !labValue) return null;
              
              const mainIssue = validation.issues.find(i => i.severity === 'error') || validation.issues[0];
              
              return (
                <div key={marker} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">
                          {marker}
                        </h4>
                        <span className="text-sm text-gray-500">
                          Current: {labValue.value} {labValue.units}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        {validation.issues.map((issue, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            {issue.severity === 'error' ? (
                              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            )}
                            <div className="text-sm">
                              <p className="text-gray-900">{issue.message}</p>
                              {issue.details && (
                                <p className="text-gray-500 text-xs mt-0.5">{issue.details}</p>
                              )}
                              {issue.suggestedFix?.units && (
                                <p className="text-blue-600 text-xs mt-1">
                                  Suggested: {labValue.value} {issue.suggestedFix.units}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setCorrectionModalData({ marker, labValue, validation })}
                      className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      Correct Value
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="verification-note">
        <p>
          <strong>Note:</strong> Always verify extracted values against the original PDF.
        </p>
      </div>
      
      {/* Correction Modal */}
      {correctionModalData && (
        <ValueCorrectionModal
          isOpen={true}
          onClose={() => setCorrectionModalData(null)}
          marker={correctionModalData.marker}
          labValue={correctionModalData.labValue}
          issues={correctionModalData.validation.issues}
          onSave={(marker, correctedValue, correctedUnit) => {
            // Call the parent's correction handler
            if (onValueCorrected) {
              onValueCorrected(marker, correctedValue, correctedUnit);
            }
            setCorrectionModalData(null);
          }}
        />
      )}
    </div>
  );
};


export default DataTable;